import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  Alert, 
  CircularProgress,
  Paper
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

function SubscriptionConfirm() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  
  useEffect(() => {
    const confirmSubscription = async () => {
      // Get token from URL parameters
      const urlParams = new URLSearchParams(location.search);
      const token = urlParams.get('token');
      
      if (!token) {
        setError('Invalid confirmation link');
        setLoading(false);
        return;
      }
      
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}candidates/api/confirm-subscription/`,
          { token }
        );
        
        setSuccess(true);
        setError('');
      } catch (error) {
        console.error('Confirmation error:', error);
        setError(error.response?.data?.error || 'Failed to confirm subscription');
        setSuccess(false);
      } finally {
        setLoading(false);
      }
    };
    
    confirmSubscription();
  }, [location.search]);
  
  const handleGoHome = () => {
    navigate('/');
  };
  
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f7ff'
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            textAlign: 'center',
            borderRadius: 3,
            maxWidth: 400
          }}
        >
          <CircularProgress size={60} sx={{ color: '#0017E7', marginBottom: 2 }} />
          <Typography variant="h6" sx={{ fontFamily: 'Poppins, sans-serif' }}>
            Confirming your subscription...
          </Typography>
        </Paper>
      </Box>
    );
  }
  
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f7ff',
        padding: 2
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            textAlign: 'center',
            borderRadius: 3
          }}
        >
          {success ? (
            <>
              <CheckCircleIcon 
                sx={{ 
                  fontSize: 80, 
                  color: '#4caf50', 
                  marginBottom: 2 
                }} 
              />
              <Typography 
                variant="h4" 
                sx={{ 
                  fontFamily: 'Kontora, sans-serif',
                  fontWeight: 700,
                  color: '#0017E7',
                  marginBottom: 2
                }}
              >
                Subscription Confirmed!
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  fontFamily: 'Poppins, sans-serif',
                  color: '#666',
                  marginBottom: 3,
                  lineHeight: 1.6
                }}
              >
                Thank you for subscribing to our newsletter! You'll now receive the latest job opportunities, career tips, and industry insights directly in your inbox.
              </Typography>
              <Button
                variant="contained"
                onClick={handleGoHome}
                sx={{
                  backgroundColor: '#0017E7',
                  color: 'white',
                  borderRadius: '12px',
                  padding: '12px 24px',
                  textTransform: 'none',
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 500,
                  fontSize: '16px',
                  '&:hover': {
                    backgroundColor: '#0012B3',
                  },
                }}
              >
                Go to Homepage
              </Button>
            </>
          ) : (
            <>
              <ErrorIcon 
                sx={{ 
                  fontSize: 80, 
                  color: '#f44336', 
                  marginBottom: 2 
                }} 
              />
              <Typography 
                variant="h4" 
                sx={{ 
                  fontFamily: 'Kontora, sans-serif',
                  fontWeight: 700,
                  color: '#f44336',
                  marginBottom: 2
                }}
              >
                Confirmation Failed
              </Typography>
              <Alert 
                severity="error" 
                sx={{ 
                  marginBottom: 3,
                  textAlign: 'left'
                }}
              >
                {error}
              </Alert>
              <Typography 
                variant="body1" 
                sx={{ 
                  fontFamily: 'Poppins, sans-serif',
                  color: '#666',
                  marginBottom: 3
                }}
              >
                The confirmation link may be invalid or expired. Please try subscribing again.
              </Typography>
              <Button
                variant="contained"
                onClick={handleGoHome}
                sx={{
                  backgroundColor: '#0017E7',
                  color: 'white',
                  borderRadius: '12px',
                  padding: '12px 24px',
                  textTransform: 'none',
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 500,
                  fontSize: '16px',
                  '&:hover': {
                    backgroundColor: '#0012B3',
                  },
                }}
              >
                Go to Homepage
              </Button>
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
}

export default SubscriptionConfirm;
